import { FC, useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import Loader from '../../loader/Loader';
import { AlertSVG, DocumentUploadIllustrationSVG, EditSVG, UploadTraySVG } from '../../svgs';
import StripeKycDocumentModal from './StripeKycDocumentModal';
import StripePayoutSetupModal from './StripePayoutSetupModal';
import { StripePayoutSetupMode } from '../../../types/enums';
import { StripeAccountStatus, StripeKycDetails, StripePayoutAddress } from '../../../types/interfaces/stripeConnect.interfaces';
import { doesStripeAccountExist, getStripeAccountStatus, getStripeKycDetails } from '../../../api/stripeConnectApi';
import {
  stripeStatusGetBadge,
  stripeStatusGetInfoBanner,
  stripeStatusIsEnabled,
  stripeStatusRequiresDocument,
} from '../../../services/stripeConnect.service';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  MARKETPLACE_CONSTANTS,
  PAYMENT_CONSTANTS,
} from '../../../constants/common';

const emptyValue = MARKETPLACE_CONSTANTS.EMPTY_VALUE;

export const SHOW_STATUS_INFO_BANNER = true;

const formatAddress = (address?: Partial<StripePayoutAddress> | null) => {
  if (!address) return emptyValue;
  const line = [address.line1, address.line2, address.city, address.state, address.postalCode, address.country]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ');
  return line || emptyValue;
};

const hasSavedKycDetails = (details: StripeKycDetails | null) =>
  Boolean(
    details?.firstName ||
      details?.lastName ||
      details?.email ||
      details?.phone ||
      details?.maskedAccountNumber ||
      details?.maskedBankRoutingNumber ||
      details?.address?.line1,
  );

export interface PayoutPaymentMethodProps {
  showStatusInfoBanner?: boolean;
}
const PayoutPaymentMethod: FC<PayoutPaymentMethodProps> = ({ showStatusInfoBanner = SHOW_STATUS_INFO_BANNER }) => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
  const [kycDetails, setKycDetails] = useState<StripeKycDetails | null>(null);
  const [accountExists, setAccountExists] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState<boolean>(false);
  const [kycMode, setKycMode] = useState<StripePayoutSetupMode>(StripePayoutSetupMode.Kyc);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  const fetchStripeStatus = useCallback(async () => {
    if (!accountId) {
      setStripeStatus(null);
      setKycDetails(null);
      setAccountExists(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data: exists } = await doesStripeAccountExist(accountId);
      const stripeAccountExists = exists === true;
      setAccountExists(stripeAccountExists);

      if (!stripeAccountExists) {
        setStripeStatus(null);
        setKycDetails(null);
        return;
      }

      const [statusResult, kycResult] = await Promise.allSettled([
        getStripeAccountStatus(accountId),
        getStripeKycDetails(accountId),
      ]);

      setStripeStatus(statusResult.status === 'fulfilled' ? statusResult.value.data ?? null : null);
      setKycDetails(kycResult.status === 'fulfilled' ? kycResult.value.data ?? null : null);
    } catch {
      setStripeStatus(null);
      setKycDetails(null);
      setAccountExists(false);
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void fetchStripeStatus();
  }, [fetchStripeStatus]);

  const openKycModal = (mode: StripePayoutSetupMode) => {
    if (!accountId) return;
    setKycMode(mode);
    setIsSetupModalOpen(true);
  };

  const onboarded = accountExists && stripeStatusIsEnabled(stripeStatus);
  const badge = stripeStatusGetBadge(stripeStatus, onboarded);
  const showDocumentUpload = stripeStatusRequiresDocument(stripeStatus);
  const showDetailsCard = hasSavedKycDetails(kycDetails);
  const showKycButton = !showDetailsCard && !showDocumentUpload;
  const bannerInfo = stripeStatusGetInfoBanner(badge?.variant, stripeStatus);
  const shouldRenderBanner = showStatusInfoBanner && bannerInfo !== null;
  const accountHolderName = [kycDetails?.firstName, kycDetails?.lastName].filter(Boolean).join(' ').trim();

  return (
    <div className="payout-payment-method">
      {isLoading ? (
        <Loader addedSpace loaderSize="small" />
      ) : (
        <>
          {shouldRenderBanner && bannerInfo && (
            <div
              className={`payout-status-info-banner payout-status-info-banner--${bannerInfo.variant}`}
              data-testid="payout-status-info-banner"
              role="alert">
              <span>{bannerInfo.message}</span>
            </div>
          )}

          {showDetailsCard && (
            <div className="card payout-method-card">
              <div className="payout-card-header">
                <h4>{PAYMENT_CONSTANTS.ACH}</h4>
                <div className="payout-card-actions">
                  {badge && (
                    <span
                      className={`payout-status-badge payout-status-badge--${badge.variant}`}
                      data-testid="payout-status-badge">
                      {badge.label}
                    </span>
                  )}
                  <button
                    className="icon button transparent edit-button"
                    data-testid="edit-payout-button"
                    onClick={() => openKycModal(StripePayoutSetupMode.Edit)}>
                    <EditSVG />
                  </button>
                </div>
              </div>
              <div className="payout-card-body">
                <div className="payout-account-number">{kycDetails?.maskedAccountNumber || emptyValue}</div>
                <div className="payout-method-row">
                  <h5>{PAYMENT_CONSTANTS.ACCOUNT_HOLDER_NAME}:</h5>
                  <p>{accountHolderName || emptyValue}</p>
                </div>
                <div className="payout-method-row">
                  <h5>{LABEL_CONSTANTS.EMAIL}:</h5>
                  <p>{kycDetails?.email || emptyValue}</p>
                </div>
                <div className="payout-method-row">
                  <h5>{LABEL_CONSTANTS.PHONE_NO}:</h5>
                  <p>{kycDetails?.phone || emptyValue}</p>
                </div>
                <div className="payout-method-row">
                  <h5>{PAYMENT_CONSTANTS.ROUTING_NUMBER}:</h5>
                  <p>{kycDetails?.maskedBankRoutingNumber || emptyValue}</p>
                </div>
                <div className="payout-method-row">
                  <h5>{LABEL_CONSTANTS.ADDRESS}:</h5>
                  <p>{formatAddress(kycDetails?.address)}</p>
                </div>
              </div>
            </div>
          )}

          {showDocumentUpload && (
            <div
              className="card payout-method-card payout-document-card"
              data-testid="payout-document-card"
              onClick={() => setIsDocumentModalOpen(true)}>
              <div className="payout-document-card-body">
                <div className="payout-document-illustration">
                  <DocumentUploadIllustrationSVG width={56} height={56} />
                </div>
                <h4 className="payout-document-title">{ANIMAL_CONSTANTS.DOCUMENTS}</h4>
                <p className="payout-document-description">{LABEL_CONSTANTS.CLICK_HERE_TO_UPLOAD_DOCUMENTS}</p>
                <div className="payout-document-pill">
                  <AlertSVG width={14} height={14} color="currentColor" />
                  <span>{LABEL_CONSTANTS.ACTION_REQUIRED}</span>
                </div>
                <button
                  type="button"
                  className="payout-document-upload-button"
                  onClick={e => {
                    e.stopPropagation();
                    setIsDocumentModalOpen(true);
                  }}
                  disabled={!accountId}>
                  <span>{LABEL_CONSTANTS.DOCUMENT_UPLOAD}</span>
                  <UploadTraySVG width={18} height={18} />
                </button>
              </div>
            </div>
          )}

          {showKycButton && (
            <div className="add-payment-method-button">
              <button className="button outlined" onClick={() => openKycModal(StripePayoutSetupMode.Kyc)} disabled={!accountId}>
                {BUTTON_CONSTANTS.ADD_PAYMENT_METHOD}
              </button>
            </div>
          )}
        </>
      )}

      {accountId != null && isSetupModalOpen && (
        <StripePayoutSetupModal
          isOpen={isSetupModalOpen}
          accountId={accountId}
          mode={kycMode}
          description={MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_SETUP_REQUIRED_TO_LIST}
          onClose={() => setIsSetupModalOpen(false)}
          onSuccess={() => {
            setIsSetupModalOpen(false);
            void fetchStripeStatus();
          }}
        />
      )}

      {accountId != null && isDocumentModalOpen && (
        <StripeKycDocumentModal
          isOpen={isDocumentModalOpen}
          accountId={accountId}
          accountStatus={stripeStatus}
          onClose={() => {
            setIsDocumentModalOpen(false);
            void fetchStripeStatus();
          }}
          onSuccess={() => {
            setIsDocumentModalOpen(false);
            void fetchStripeStatus();
          }}
        />
      )}
    </div>
  );
};

export default PayoutPaymentMethod;
